import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useOnSaved } from '../../../hooks/useOnSaved';
import { Client, AdminFormData } from '../../../data/models/client.model';
import {
  fetchLookupItemsAsync,
  selectUserCategoryTypes,
} from '../../../data/lookup.slice';
import {
  createClientAdminAsync,
  selectSavedAdmin,
  selectSavingAdmin,
} from '../../../data/client.slice';
import {
  fetchUserRolesAsync,
  selectUserRoles,
} from '../../../data/security.slice';
import TextInput from '../../forms/TextInput';

const PREFIX = 'AdminDialog';

const classes = {
  content: `${PREFIX}-content`,
  nameFields: `${PREFIX}-nameFields`,
  actions: `${PREFIX}-actions`,
  progress: `${PREFIX}-progress`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    width: 500,
  },

  [`& .${classes.nameFields}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    '& > *': {
      flex: 1,
    },
  },

  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },

  [`& .${classes.progress}`]: {
    width: '100%',
    color: theme.palette.primary.main,
  },
}));

interface AdminDialogProps {
  title: string;
  open: boolean;
  client: Client;
  close: () => void;
  userRoleIDs: number[];
  userCategoryTypeIDs: number[];
}

export default function AdminDialog({
  title,
  open,
  client,
  close,
  userRoleIDs,
  userCategoryTypeIDs,
}: AdminDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const savingAdmin = useAppSelector(selectSavingAdmin);
  const savedAdmin = useAppSelector(selectSavedAdmin);
  const userCategoryTypes = useAppSelector(selectUserCategoryTypes);
  const userRoles = useAppSelector(selectUserRoles);

  const formHook = useForm<AdminFormData>({
    mode: 'onTouched',
  });

  const submitButtonEnabled =
    !savingAdmin &&
    formHook.formState.isValid &&
    userCategoryTypes.length > 0 &&
    userRoles.length > 0;

  const handleClose = () => {
    formHook.reset();
    close();
  };

  const handleSave: SubmitHandler<AdminFormData> = (data) => {
    if (userRoleIDs.length > 0) {
      const newAdmin = {
        emailAddress: data.email,
        userFirstName: data.firstName,
        userLastName: data.lastName,
      };
      dispatch(
        createClientAdminAsync(
          client.clientBC,
          newAdmin,
          userRoleIDs.filter((id): id is number => id !== undefined),
        ),
      );
    }
  };

  useOnSaved(savedAdmin, handleClose);

  // Keyboard event handler for dialog shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keyboard events when dialog is open
      if (!open) return;

      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          // Only submit if form is valid and not currently saving
          if (formHook.formState.isValid && !savingAdmin) {
            formHook.handleSubmit(handleSave)();
          }
          break;
        default:
          break;
      }
    };

    // Add event listener when dialog is open
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, formHook.formState.isValid, savingAdmin, formHook]);

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {title}
        {savingAdmin && <LinearProgress className={classes.progress} />}
      </DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <Box className={classes.nameFields}>
            <TextInput
              name="firstName"
              label={t('passenger.firstName')}
              control={formHook.control}
              disabled={false}
              required={true}
              rules={{
                required: requiredFieldMessage,
              }}
            />
            <TextInput
              name="lastName"
              label={t('passenger.lastName')}
              control={formHook.control}
              disabled={false}
              required={true}
              rules={{
                required: requiredFieldMessage,
              }}
            />
          </Box>
          <TextInput
            name="email"
            label={t('client.email')}
            control={formHook.control}
            disabled={false}
            required={true}
            rules={{
              required: requiredFieldMessage,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('common.invalidEmailAddress'),
              },
            }}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!submitButtonEnabled}
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.add')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
